import { FC } from 'react'
import CN from 'classnames'

import { Button } from 'components/atoms/Button'

import { ProcessBadge } from '../ProcessBadge'
import {
  CatalogueOutcome,
  EmailAlertGroupPrefill,
  OUTCOME_FAILURE,
  OUTCOME_SUCCESS,
  PairedCardRow,
  serviceNameForOutcome,
} from '../processCatalogue'

import { CardAuditLine } from './CardAuditLine'
import {
  CardActions,
  NoRecipientsHint,
  NotificationRecipientCard,
} from './EmailAlertReportCard'

export interface EmailAlertPairedCardProps {
  row: PairedCardRow
  showPartnerLabel: boolean
  canEdit: boolean
  onManageClick: (card: NotificationRecipientCard) => void
  onEditClick: (card: NotificationRecipientCard) => void
  onDeleteClick: (card: NotificationRecipientCard) => void
  onSetUpClick: (prefill: EmailAlertGroupPrefill) => void
}

interface OutcomeSectionProps {
  row: PairedCardRow
  outcome: CatalogueOutcome
  card: NotificationRecipientCard | null
  canEdit: boolean
  onManageClick: (card: NotificationRecipientCard) => void
  onEditClick: (card: NotificationRecipientCard) => void
  onDeleteClick: (card: NotificationRecipientCard) => void
  onSetUpClick: (prefill: EmailAlertGroupPrefill) => void
}

const OutcomeSection: FC<OutcomeSectionProps> = ({
  row,
  outcome,
  card,
  canEdit,
  onManageClick,
  onEditClick,
  onDeleteClick,
  onSetUpClick,
}: OutcomeSectionProps) => {
  const serviceName = card
    ? card.serviceName
    : serviceNameForOutcome(row.process, outcome)

  return (
    <div
      className={CN('rounded-md px-4 py-3 flex flex-col gap-2.5 min-w-0', {
        'border border-N-200 bg-white': card !== null,
        'border-[1.5px] border-dashed border-N-300 bg-N-50': card === null,
      })}>
      <div className='flex items-center gap-2 flex-wrap'>
        <span
          className={CN('w-[7px] h-[7px] rounded-full', {
            'bg-G-500': outcome === OUTCOME_SUCCESS,
            'bg-R-500': outcome === OUTCOME_FAILURE,
          })}></span>
        <span className='text-[11.5px] font-bold tracking-wide text-N-600 uppercase'>
          {outcome}
        </span>
        <span className='text-[12px] text-N-500 font-mono' title='Service name'>
          {serviceName}
        </span>
      </div>

      {card ? (
        <>
          <CardActions
            card={card}
            canEdit={canEdit}
            onManageClick={onManageClick}
            onEditClick={onEditClick}
            onDeleteClick={onDeleteClick}
          />
          {card.recipientCount === 0 && (
            <NoRecipientsHint
              card={card}
              canEdit={canEdit}
              onManageClick={onManageClick}
            />
          )}
          <CardAuditLine
            lastModifiedBy={card.lastModifiedBy}
            lastModifiedOn={card.lastModifiedOn}
          />
        </>
      ) : (
        <div className='flex items-center gap-3 flex-wrap'>
          <span className='text-[13px] text-N-500'>
            Not configured — emails for this process are not sent.
          </span>
          {canEdit && (
            <Button
              appearance='outline'
              size='xs'
              onClick={() =>
                onSetUpClick({
                  partnerId: row.partner.id,
                  partnerName: row.partner.name,
                  partnerCode: row.partner.code,
                  process: row.process,
                  outcome,
                })
              }>
              <i className='ri-add-line pr-1'></i>
              Set up
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

/** BDL20-4262: the Success and Failure groups of one process, side by side in one card. */
export const EmailAlertPairedCard: FC<EmailAlertPairedCardProps> = ({
  row,
  showPartnerLabel,
  canEdit,
  onManageClick,
  onEditClick,
  onDeleteClick,
  onSetUpClick,
}: EmailAlertPairedCardProps) => {
  const isPlaceholder = row.success === null && row.failure === null
  const sectionProps = {
    row,
    canEdit,
    onManageClick,
    onEditClick,
    onDeleteClick,
    onSetUpClick,
  }

  return (
    <div
      className={CN('rounded-md px-5 py-[18px] flex flex-col gap-3', {
        'border border-N-200': !isPlaceholder,
        'border-[1.5px] border-dashed border-N-300 bg-N-50': isPlaceholder,
      })}>
      <div className='flex items-center gap-2.5 flex-wrap'>
        <span
          className={CN('text-[15.5px] font-bold', {
            'text-N-800': !isPlaceholder,
            'text-N-600': isPlaceholder,
          })}>
          {row.displayName}
        </span>
        <ProcessBadge label={row.tag} />
        {showPartnerLabel && (
          <span className='text-[11px] font-semibold text-N-500 bg-N-100 rounded px-2 py-[2px]'>
            {row.partner.name} ({row.partner.code})
          </span>
        )}
        {isPlaceholder && (
          <span className='text-[11px] font-semibold text-N-500 border border-N-300 rounded px-2 py-[2px]'>
            Not configured
          </span>
        )}
      </div>

      <div className='grid grid-cols-1 lg:grid-cols-2 gap-3'>
        <OutcomeSection
          {...sectionProps}
          outcome={OUTCOME_SUCCESS}
          card={row.success}
        />
        <OutcomeSection
          {...sectionProps}
          outcome={OUTCOME_FAILURE}
          card={row.failure}
        />
      </div>
    </div>
  )
}

export default EmailAlertPairedCard
